import { EventObject } from '../types'
import {
  ConsentUpdatedType,
  ContactsInviteType,
  OrderCancelDeniedReason,
  OrderCancelDeniedType,
  RmaCaseReasonType,
  RmaCreateCaseType,
  RmaOrderItemType,
  SimpleEventAction,
  SimpleEventType,
  NavigationInteractionType,
  NavigationItemEventType,
  NavigationExtras,
  SearchInteractionItemEventType,
  SearchShortcutTypes,
  SearchShortcutInteractionEventType,
  FacetClickEventType,
  SortClickEventType,
  FilterCatClickEventType,
  CreateAccountStartHeaderType,
  CreateAccountStartFooterType,
  CreateAccountStartCheckoutType,
  CreateAccountEmailType,
  CreateAccountCompanyType,
  CreateAccountCreateType,
  CreateAccountCompletedType,
  CatMenuItemClickEventType,
  NavigationEventType,
  AccountLoginType,
  LoginUserType,
  AddressEventType,
  DigitalDataAddressType,
  DigitalDataAddressAction,
  CartNotificationAction,
  GA4Promotion,
  GA4ViewPromotionEvent,
  GA4SelectPromotionEvent,
} from './digital-data-types'
import { transformValuesToKebabCase, Event } from './values-to-kebabcase'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DigitalData: any
  }
}

export const DigitalData = {
  pushSimpleEvent: (gaEvt: SimpleEventAction) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: SimpleEventType = {
      event: gaEvt,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushNotifyMeEvent: (
    interaction:
      | 'notify_me_open_pop_up'
      | 'cancel_monitor_product'
      | 'monitor_product_success'
      | 'monitor_product_already_subcribed'
      | 'notify_me_generic_error'
  ) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event = {
      event: 'notify_me',
      event_info: {
        interaction,
      },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },
  pushContactInviteEvent: (action: 'sent' | 'delete' | 'update') => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: ContactsInviteType = {
      intvite_action: action,
      event: 'contact_item_invite',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushOrderCancelDenied: (reason: OrderCancelDeniedReason) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: OrderCancelDeniedType = {
      event: 'order_cancel_denied',
      reason,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushAddressEvent: (action: DigitalDataAddressAction, addressType?: DigitalDataAddressType) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: AddressEventType = {
      event: `address_${action}`,
      address_type: addressType,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushRmaCreateCase: (rma_create_case_reason: RmaCaseReasonType, items: RmaOrderItemType[]) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: RmaCreateCaseType = {
      event: 'rma_create_case',
      rma_create_case_reason,
      items,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  accountLogin: (loginSelection: string) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: AccountLoginType = {
      event: 'login_initiated',
      event_info: {
        selection: loginSelection,
      },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  loginUser: (userId: string | null) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    if (!userId) return

    const hasLoggedInUser = window.DigitalData.find(
      (item: LoginUserType) => item?.event_info?.userId === userId
    )

    if (hasLoggedInUser) return

    window.DigitalData.push({ user_id: userId })
  },

  createAccountStartHeader: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: CreateAccountStartHeaderType = {
      event: 'create_account_initiated_header',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountStartFooter: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: CreateAccountStartFooterType = {
      event: 'create_account_initiated_footer',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountStartCheckout: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: CreateAccountStartCheckoutType = {
      event: 'create_account_initiated_checkout',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  creatAccountEmail: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: CreateAccountEmailType = {
      event: 'create_account_email',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  creatAccountCompany: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: CreateAccountCompanyType = {
      event: 'create_account_company',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  creatAccountCreate: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: CreateAccountCreateType = {
      event: 'create_account_create',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountCompeted: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: CreateAccountCompletedType = {
      event: 'create_account_completed',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountVerify: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event = {
      event: 'create_account_verify',
    }
    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountEmailSucceed: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event = {
      event: 'create_account_email_succeeded',
    }
    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountLoginEmail: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: AccountLoginType = {
      event: 'login_initiated',
      event_info: {
        selection: 'register-email',
      },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountLoginRegister: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: AccountLoginType = {
      event: 'login_initiated',
      event_info: {
        selection: 'register',
      },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountLoginComplete: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: AccountLoginType = {
      event: 'login_initiated',
      event_info: {
        selection: 'register-completed',
      },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountCompanyExists: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event = {
      event: 'create_account_company_exists',
    }
    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountCompanyRequestOpen: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event = {
      event: 'create_account_company_request_open',
    }
    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountCompanyRequestSend: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event = {
      event: 'create_account_company_request_send',
    }
    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountCompanyRequestConfirm: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event = {
      event: 'create_account_company_request_confirmation',
    }
    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  createAccountCompanyCheckout: () => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event = {
      event: 'create_account_company_checkout',
    }
    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushConsentUpdated: (props: {
    analytics: boolean
    marketing: boolean
    personalization: boolean
  }) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const toGrantedOrDenied = (value: boolean) => (value ? 'granted' : 'denied')
    const event: ConsentUpdatedType = {
      event: 'consent_updated',
      ad_storage: toGrantedOrDenied(props.marketing),
      analytics_storage: toGrantedOrDenied(props.analytics),
      ad_personalization: toGrantedOrDenied(props.marketing),
      ad_user_data: toGrantedOrDenied(props.marketing),
    }

    window.DigitalData.push(event)
  },

  pushNavigationInteraction: (
    event: NavigationEventType,
    selection: string,
    interaction: NavigationInteractionType,
    extras?: NavigationExtras
  ) => {
    if (!selection) {
      console.error(`Tracking: selection with interaction ${interaction} is undefined`)
      return
    }

    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const { selectionType } = extras ?? {}

    let eventSelection = selection

    if (!extras?.disableSelectionSanitize) {
      // we sanitize data to send into GA4
      eventSelection = selection
        .split(' ')
        .map(word => {
          // replace / with - as separator and then remove additional special characters
          const sanitizedWord = word.replace(/[/]/g, '-').replace(/["&+”.(),']/g, '')

          //check if word is alphanumeric
          return /^[a-zA-Z0-9-]+$/.test(sanitizedWord) ? sanitizedWord : null
        })
        .filter(Boolean)
        .join('-')
        .toLowerCase()
    }

    const eventData: NavigationItemEventType = {
      event,
      event_info: {
        interaction,
        selection: eventSelection,
        ...(selectionType && { selectionType }),
      },
    }

    window.DigitalData.push(eventData)
  },

  pushSearchFieldInteraction: () => {
    DigitalData.pushEvent(
      {
        event: 'click_search_dropdown',
      } as SearchInteractionItemEventType,
      false
    )
  },

  pushSearchDropdownState: (
    state: 'open' | 'close',
    type: 'autocomplete' | 'initiate' | null,
    alreadyEmittedTrackingEvent: string
  ) => {
    if (!type) {
      return
    }

    const interaction = `${state}_${type}_search_dropdown`

    if (alreadyEmittedTrackingEvent === interaction) {
      return
    }

    DigitalData.pushEvent({
      event: 'search_dropdown',
      event_info: {
        interaction,
      },
    })

    return interaction
  },

  pushSearchDropdownInteraction: (
    selection: string,
    searchPhrase?: string,
    search_shortcut?: string
  ) => {
    const type = searchPhrase && searchPhrase?.length > 1 ? 'autocomplete' : 'initiate'

    DigitalData.pushEvent({
      event: 'search_dropdown',
      event_info: {
        interaction: `link_click_${type}`,
        selection,
        ...(searchPhrase && { search_term_dropdown: searchPhrase }),
        ...(search_shortcut && type === 'autocomplete' && { search_shortcut }),
      },
    })
  },

  pushSearchShortcutInteraction: (selection: string, searchShortcut: SearchShortcutTypes) => {
    DigitalData.pushEvent(
      {
        event: 'autocomplete_search_dropdown_executed',
        event_info: {
          interaction: 'link_click',
          selection: selection,
          search_shortcut: searchShortcut,
        },
      } as SearchShortcutInteractionEventType,
      !selection || !searchShortcut
    )
  },

  pushFacetClick: (selection: string) => {
    DigitalData.pushEvent(
      {
        event: 'filter',
        event_info: {
          interaction: 'select_filter',
          filter_category: selection,
        },
      } as FacetClickEventType,
      !selection
    )
  },
  pushFilterCatClick: (selection: string) => {
    DigitalData.pushEvent(
      {
        event: 'filter',
        event_info: {
          interaction: 'link_click',
          selection: selection,
        },
      } as FilterCatClickEventType,
      !selection
    )
  },
  pushSortClick: (selection: string) => {
    DigitalData.pushEvent(
      {
        event: 'sorting',
        event_info: {
          interaction: 'select_sorting',
          selection: selection,
        },
      } as SortClickEventType,
      !selection
    )
  },
  pushCatMenuItemClick: (selection: string) => {
    DigitalData.pushEvent(
      {
        event: 'category_menu',
        event_info: {
          interaction: 'link_click',
          selection: selection,
        },
      } as CatMenuItemClickEventType,
      !selection
    )
  },

  pushCartNotificationEvent: (action: CartNotificationAction) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event = {
      event: 'add_to_cart_popup',
      interaction: action,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushEvent: (event: Event, empty?: boolean) => {
    if (empty) {
      console.error('Tracking: event data is undefined')
      return
    }

    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const kebabCaseEvent = transformValuesToKebabCase(event)
    window.DigitalData.push(kebabCaseEvent)
  },

  pushViewPromotion: (promotions: GA4Promotion) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event: GA4ViewPromotionEvent = {
      event: 'view_promotion',
      ecommerce: promotions,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushSelectPromotion: (promotions: GA4Promotion) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: GA4SelectPromotionEvent = {
      event: 'select_promotion',
      ecommerce: promotions,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },
}
